import { datadogLogs } from '@datadog/browser-logs';
import type { Editor, JSONContent } from '@tiptap/core';
import Color from '@tiptap/extension-color';
import { FileHandler } from '@tiptap/extension-file-handler';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { TaskItem, TaskList } from '@tiptap/extension-list';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { EDITOR_LOG_PREFIX } from '@web-app/utils/api/logger-prefixes';
import { MutableRefObject, useState } from 'react';

import { EditorName } from './constants';
import { CalloutNode } from './extensions/CalloutNode';
import { LineHeight } from './extensions/LineHeight';

const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
];

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

/**
 * Drop and paste handlers for the image FileHandler.
 *
 * Extracted so the branch below is unit-testable: the handlers are otherwise
 * buried in a `useEditor` config that only a real paste event can reach.
 *
 * When `onImageFiles` is supplied the file is handed over and nothing is
 * inserted, so the editor HTML stays free of data URIs. Otherwise the image is
 * embedded inline as base64, which is the long-standing behaviour for editors
 * whose HTML is what gets stored.
 */
export const createImageFileHandlers = ({
  editorName,
  onImageFiles,
}: {
  editorName: EditorName;
  onImageFiles?: (files: File[]) => void;
}) => {
  const embed = async (
    files: File[],
    insert: (src: string) => void,
    failureLabel: string,
  ) => {
    await Promise.all(
      files.map(async (file) => {
        try {
          insert(await fileToBase64(file));
        } catch (error) {
          datadogLogs.logger.error(
            `${EDITOR_LOG_PREFIX} ${failureLabel}`,
            {
              fileName: file.name,
              fileType: file.type,
              editorName,
            },
            error instanceof Error ? error : undefined,
          );
        }
      }),
    );
  };

  return {
    onDrop: async (currentEditor: Editor, files: File[], pos: number) => {
      if (onImageFiles) {
        onImageFiles(files);
        return;
      }
      await embed(
        files,
        (src) =>
          currentEditor
            .chain()
            .focus()
            .insertContentAt(pos, { type: 'image', attrs: { src } })
            .run(),
        'image drop failed',
      );
    },
    onPaste: async (currentEditor: Editor, files: File[]) => {
      if (onImageFiles) {
        onImageFiles(files);
        return;
      }
      await embed(
        files,
        (src) =>
          currentEditor
            .chain()
            .focus()
            .insertContent({ type: 'image', attrs: { src } })
            .run(),
        'image paste failed',
      );
    },
  };
};

export interface UseTipTapEditorOptions {
  initialContent: string | object;
  editable: boolean;
  placeholder: string;
  name: EditorName;
  /** Accessible name for the contenteditable itself, so a screen reader
   * announces which field has focus. Optional — omitted leaves the current
   * behaviour unchanged. */
  ariaLabel?: string;
  onChange?: (contentHtml: string, contentJson: JSONContent) => void;
  onEditorReady?: (editor: any) => void;
  isInitializing: MutableRefObject<boolean>;
  /**
   * Takes dropped and pasted images instead of embedding them in the document.
   *
   * Left unset, an image becomes a base64 data URI inside the editor HTML.
   * That is fine where the HTML is what gets stored — Healthie's note `content`
   * renders it back as an image. It is not fine for an editor whose content is
   * converted to Markdown and sent through `sendChatMessage`: the data URI
   * survives as `![](data:...)` and GetStream rejects the message at 413 once
   * the payload passes 100KB, which is roughly a 73KB image.
   *
   * Those editors pass this so the file goes to the attachment upload the
   * paperclip button already uses, and only prose reaches the message body.
   */
  onImageFiles?: (files: File[]) => void;
}

/**
 * Custom hook that configures and creates the TipTap editor instance.
 * Handles extension setup, file handling, and initialization.
 */
export const useTipTapEditor = ({
  initialContent,
  editable,
  placeholder,
  name,
  ariaLabel,
  onChange,
  onEditorReady,
  isInitializing,
  onImageFiles,
}: UseTipTapEditorOptions) => {
  const [isReady, setIsReady] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: false,
        underline: false,
      }),
      TextStyle,
      Color.configure({
        types: ['textStyle'],
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      LineHeight.configure({
        types: ['heading', 'paragraph'],
        lineHeights: ['1', '1.15', '1.5', '2'],
        defaultLineHeight: '1.15',
      }),
      Link.configure({
        openOnClick: true,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
          class: 'tiptap-link',
        },
        autolink: true,
        linkOnPaste: true,
      }),
      CalloutNode,
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
      Image.configure({
        inline: true,
        allowBase64: true,
        HTMLAttributes: {
          class: 'tiptap-image',
        },
      }),
      FileHandler.configure({
        // https://tiptap.dev/docs/editor/extensions/functionality/filehandler
        allowedMimeTypes: ALLOWED_IMAGE_TYPES,
        ...createImageFileHandlers({ editorName: name, onImageFiles }),
      }),
    ],
    content: '',
    immediatelyRender: false,
    editable,
    editorProps: {
      attributes: {
        class: 'tiptap-editor',
        'data-placeholder': placeholder,
        ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
      },
    },
    onUpdate: ({ editor }) => {
      if (isInitializing.current) {
        return;
      }
      if (!onChange) return;
      const html = editor.getHTML();
      const json = editor.getJSON();
      onChange(html, json);
    },
    onCreate: ({ editor }) => {
      if (initialContent) {
        try {
          editor.commands.setContent(initialContent, { emitUpdate: false });
        } catch (error) {
          datadogLogs.logger.error(
            `${EDITOR_LOG_PREFIX} initial content load failed`,
            {
              contentType: typeof initialContent,
              editorName: name,
            },
            error instanceof Error ? error : undefined,
          );
        }
      }
      setIsReady(true);
      // Call onEditorReady callback if provided
      if (onEditorReady) {
        onEditorReady(editor);
      }
      setTimeout(() => {
        isInitializing.current = false;
      }, 0);
    },
  });

  return { editor, isReady };
};
